// ─────────────────────────────────────────────────────────────────────────────
// FEATURE: Menu  (cut customization — the "Choose your cut" step of the pack sheet)
// The ways each catch can be cut/cleaned, per catch. The customer picks one cut per catch (it applies to every pack
// of that catch in the cart); it is saved with the cart line, shown in the cart and checkout, and written into the
// order line's name, e.g. "Vanjaram Kingfish (500 g, Curry Cut)", so the kitchen team sees it.
//
// BUSINESS-CONFIGURABLE: the option names, which catch offers which cut, and the prep fees below are the owner's
// decisions (the fees and names follow the supplied design). `prepFeePerKg` is charged per kg of the pack — a 500 g
// pack pays half — and 0 shows as "FREE". Keep descriptions to what the kitchen really does: no thickness/precision
// figures and no packaging promises unless they are true.
// ─────────────────────────────────────────────────────────────────────────────
import type { PackSize } from "./packs";
import type { SeafoodItem } from "./seafood";

export type CutId =
  | "curry"
  | "steaks"
  | "whole-cleaned"
  | "fillet-cubes"
  | "prawn-whole"
  | "prawn-deveined"
  | "crab-whole"
  | "crab-curry"
  | "squid-whole"
  | "squid-rings"
  | "lobster-whole"
  | "lobster-split";

export interface CutOption {
  id: CutId;
  name: string;
  /** Short "best for" hint shown as a chip. */
  tag?: string;
  description: string;
  /** Rupees charged per kg for the preparation; 0 = free. */
  prepFeePerKg: number;
}

const LIBRARY: Record<CutId, CutOption> = {
  curry: {
    id: "curry",
    name: "Curry Cut",
    tag: "Ideal for curries",
    description: "Cut into medium pieces that cook evenly in gravies and stews.",
    prepFeePerKg: 0,
  },
  steaks: {
    id: "steaks",
    name: "Steaks / Bengali Cut",
    tag: "Ideal for fry & roast",
    description: "Thick, even slices across the fish. Holds its shape for a tawa fry or a spicy rava crust.",
    prepFeePerKg: 0,
  },
  "whole-cleaned": {
    id: "whole-cleaned",
    name: "Whole Cleaned & Gutted (Head-On)",
    tag: "Ideal for grill & bake",
    description: "Scales and innards removed; the whole fish stays intact, ready for tandoori marinades or oven bakes.",
    prepFeePerKg: 0,
  },
  "fillet-cubes": {
    id: "fillet-cubes",
    name: "Boneless Fillet Cubes",
    tag: "Kid-friendly boneless",
    description: "Deboned, bite-sized cubes of tender flesh for quick cooking.",
    prepFeePerKg: 40,
  },
  "prawn-whole": {
    id: "prawn-whole",
    name: "Whole (Head-On)",
    tag: "Ideal for curries & fry",
    description: "Prawns as landed, rinsed and ready to cook in the shell.",
    prepFeePerKg: 0,
  },
  "prawn-deveined": {
    id: "prawn-deveined",
    name: "Cleaned & Deveined",
    tag: "Ready to cook",
    description: "Heads removed and deveined, so they go straight into the pan.",
    prepFeePerKg: 0,
  },
  "crab-whole": {
    id: "crab-whole",
    name: "Whole Cleaned",
    tag: "Ideal for steaming",
    description: "Scrubbed and rinsed, kept whole.",
    prepFeePerKg: 0,
  },
  "crab-curry": {
    id: "crab-curry",
    name: "Curry Cut",
    tag: "Ideal for curries",
    description: "Cut into serving pieces with the claws cracked so masala gets in.",
    prepFeePerKg: 0,
  },
  "squid-whole": {
    id: "squid-whole",
    name: "Whole Cleaned",
    tag: "Ideal for stuffing & grill",
    description: "Cleaned and ready, kept whole.",
    prepFeePerKg: 0,
  },
  "squid-rings": {
    id: "squid-rings",
    name: "Cut into Rings",
    tag: "Ideal for fry & roast",
    description: "Cleaned and sliced into rings.",
    prepFeePerKg: 0,
  },
  "lobster-whole": {
    id: "lobster-whole",
    name: "Whole",
    tag: "Ideal for steaming & grill",
    description: "Whole lobster, rinsed and ready to cook.",
    prepFeePerKg: 0,
  },
  "lobster-split": {
    id: "lobster-split",
    name: "Split & Cleaned",
    tag: "Ideal for grill & butter roast",
    description: "Split lengthwise and cleaned, ready to grill.",
    prepFeePerKg: 0,
  },
};

/** Which cuts each catch is offered in. The first is the default for that catch. Every catch has at least one. */
const CUTS_BY_ITEM: Record<string, CutId[]> = {
  "vanjaram-kingfish": ["curry", "steaks", "whole-cleaned", "fillet-cubes"],
  "silver-pomfret": ["whole-cleaned", "curry", "fillet-cubes"],
  "karimeen-pearlspot": ["whole-cleaned", "curry"],
  "indian-mackerel": ["whole-cleaned", "curry"],
  "red-snapper": ["whole-cleaned", "curry", "steaks", "fillet-cubes"],
  "tiger-prawns": ["prawn-whole", "prawn-deveined"],
  "mud-crab": ["crab-curry", "crab-whole"],
  squid: ["squid-whole", "squid-rings"],
  "spiny-lobster": ["lobster-whole", "lobster-split"],
};

/** Used for any catch added to the menu before its cuts are listed above. */
const FALLBACK: CutId[] = ["whole-cleaned"];

/** The cuts a catch is offered in, default first. */
export const cutsFor = (item: SeafoodItem): CutOption[] => (CUTS_BY_ITEM[item.id] ?? FALLBACK).map((id) => LIBRARY[id]);

export const defaultCut = (item: SeafoodItem): CutOption => cutsFor(item)[0];

/** The chosen cut, or the catch's default when the id is missing or no longer offered (e.g. an old saved cart). */
export const cutOf = (item: SeafoodItem, id: string | undefined): CutOption => cutsFor(item).find((cut) => cut.id === id) ?? defaultCut(item);

/** Prep fee for ONE pack of this size, in rupees. */
export const prepFee = (cut: CutOption, pack: PackSize): number => Math.round(cut.prepFeePerKg * pack.kg);

/** "FREE" or "+₹40/kg prep". */
export const prepLabel = (cut: CutOption): string => (cut.prepFeePerKg > 0 ? `+₹${cut.prepFeePerKg}/kg prep` : "FREE");

/** Every cut the kitchen offers, by id — the admin product studio lets staff pick from these. */
export const CUT_LIBRARY: Record<CutId, CutOption> = LIBRARY;
